import { assert, expect } from 'chai';
import { APIDocumentation } from '../support/apiDocuments';
import { handleFail, wrapPromise } from '../../utils';
import { IntentUtilityContext } from '../../context-types';
import { MetadataFdc3Api } from '../support/metadata-support';
import { ContextType, ControlContextType, Intent, IntentApp, RaiseIntentControl } from '../support/intent-support';
import { AppIdentifier, DesktopAgent, getAgent, IntentResolution, Listener } from '@finos/fdc3';

const findInstancesDocs = '\r\nDocumentation: ' + APIDocumentation.findInstances + '\r\nCause: ';

export default async () =>
  describe('fdc3.findInstances', function () {
    this.timeout(5000);

    let control: RaiseIntentControl;
    let fdc3: DesktopAgent;

    beforeEach(async () => {
      fdc3 = await getAgent();
      control = new RaiseIntentControl(fdc3);
    });

    afterEach(async function afterEach() {
      await control.closeMockAppIfOpened(this.currentTest?.title ?? 'Unknown Test');
    });

    const findInstances = '(FindInstances) valid appID when opening multiple instances of the same app';
    it(findInstances, async () => {
      const api = new MetadataFdc3Api();
      let listener: Listener | undefined;

      try {
        const appIdentifier = await control.openIntentApp(IntentApp.IntentAppA, findInstances); // open IntentAppA
        const appIdentifier2 = await control.openIntentApp(IntentApp.IntentAppA, findInstances); // open second instance of IntentAppA

        //confirm that the instanceId for both app instantiations is different
        expect(
          appIdentifier.instanceId,
          `The AppIdentifier's instanceId property for both instances of the opened app should not be the same.${findInstancesDocs}`
        ).to.not.equal(appIdentifier2.instanceId);

        const instances = await control.findInstances(IntentApp.IntentAppA);
        validateInstances(instances, appIdentifier, appIdentifier2);

        const wrapper = wrapPromise();
        const appControlChannel = await api.retrieveAppControlChannel();

        //ensure appIdentifier received the raised intent
        listener = await appControlChannel.addContextListener(
          ControlContextType.A_TESTING_INTENT_LISTENER_TRIGGERED,
          (context: IntentUtilityContext) => {
            expect(
              context['instanceId'],
              'the raised intent was received by a different instance of the mock app than expected'
            ).to.be.equals(appIdentifier.instanceId);
            wrapper.resolve();
          }
        );

        const resolution = await api.raiseIntent(Intent.aTestingIntent, ContextType.testContextX, appIdentifier); // raise an intent that targets appIdentifier
        validateResolutionSource(resolution, appIdentifier);
        await wrapper.promise; // wait for context from IntentAppA
      } catch (ex) {
        handleFail(findInstancesDocs, ex);
      } finally {
        if (listener) {
          control.unsubscribeListener(listener);
        }
      }
    });
  });

function validateResolutionSource(resolution: IntentResolution, appIdentifier: AppIdentifier) {
  // check that resolution.source matches the appIdentifier
  expect(
    resolution.source.appId,
    "IntentResolution.source.appId did not match the mock app's AppIdentifier's appId"
  ).to.be.equal(appIdentifier.appId);
  expect(
    resolution.source.instanceId,
    "IntentResolution.source.instanceId did not match the mock app's AppIdentifier's instanceId"
  ).to.be.equal(appIdentifier.instanceId);
}

function validateInstances(instances: AppIdentifier[], appIdentifier: AppIdentifier, appIdentifier2: AppIdentifier) {
  // check that the retrieved instances match both the retrieved appIdentifiers
  const compareAppIdentifiers = (a: AppIdentifier, b: AppIdentifier) =>
    a.appId === b.appId && a.instanceId === b.instanceId;

  if (!(
    instances.some(instance => compareAppIdentifiers(instance, appIdentifier)) &&
    instances.some(instance => compareAppIdentifiers(instance, appIdentifier2))
  )) {
    assert.fail(
      `At least one AppIdentifier object is missing from the AppIdentifier array returned after calling fdc3.findInstances(app: AppIdentifier)${findInstancesDocs}`
    );
  }
}
